// Owner.tsx
import React from 'react';
import {
  Grid,
  TextField,
  MenuItem,
  Typography,
  Button,
  Tooltip
} from '@mui/material';
import STATES from '../../constants/states';
import { isValidZipCode, isValidTaxId } from '../../utils/validators';
import { Owner as OwnerType } from 'form-application-shared';

interface OwnerProps {
  owner: OwnerType;
  producerIndex: number;
  ownerIndex: number;
  handleOwnerChange: (producerIndex: number, ownerIndex: number, field: keyof OwnerType, value: string | number) => void;
  removeOwner: (producerIndex: number, ownerIndex: number) => void;
  validationMode: boolean;
  fieldErrors: Record<number, any> | undefined;
  canDelete: boolean;
}

const Owner: React.FC<OwnerProps> = ({ 
  owner, 
  producerIndex, 
  ownerIndex, 
  handleOwnerChange, 
  removeOwner, 
  validationMode, 
  fieldErrors,
  canDelete
}) => {
  // Get error state for a specific field
  const getFieldError = (fieldName: keyof OwnerType): boolean => {
    if (!validationMode) return false;
    return !!fieldErrors?.[producerIndex]?.owners?.[ownerIndex]?.[fieldName];
  };

  // Get error message for a specific field
  const getErrorMessage = (fieldName: keyof OwnerType): string => {
    if (!validationMode) return "";
    
    if (fieldName === 'email' && getFieldError(fieldName)) {
      return "Invalid email format";
    }
    if (fieldName === 'taxId' && owner.taxId && !isValidTaxId(owner.taxId)) {
      return "Invalid Tax ID format (use SSN: XXX-XX-XXXX or EIN: XX-XXXXXXX)";
    }
    if (fieldName === 'zip' && owner.zip && !isValidZipCode(owner.zip)) {
      return "Invalid ZIP code format (use XXXXX or XXXXX-XXXX)";
    }
    
    return getFieldError(fieldName) ? `${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)} is required` : "";
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} container justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle2">Owner {ownerIndex + 1}</Typography>
        <Tooltip title={canDelete ? "" : "At least one owner is required"}>
          <span>
            <Button
              variant="text"
              color="error"
              onClick={() => removeOwner(producerIndex, ownerIndex)}
              disabled={!canDelete}
            >
              Delete Owner
            </Button>
          </span>
        </Tooltip>
      </Grid>
      <Grid item xs={12}>
        <TextField
          id={`owner-${producerIndex}-${ownerIndex}-legalName`}
          label="Legal Name"
          name="legalName"
          value={owner.legalName || ''}
          onChange={(e) => handleOwnerChange(producerIndex, ownerIndex, 'legalName', e.target.value)}
          fullWidth
          error={getFieldError('legalName')}
          helperText={getErrorMessage('legalName')}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          id={`owner-${producerIndex}-${ownerIndex}-email`}
          label="Email"
          name="email"
          type="email"
          value={owner.email || ''}
          onChange={(e) => handleOwnerChange(producerIndex, ownerIndex, 'email', e.target.value)}
          fullWidth
          error={getFieldError('email')}
          helperText={getErrorMessage('email')}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          id={`owner-${producerIndex}-${ownerIndex}-taxId`}
          label="Tax ID"
          name="taxId"
          value={owner.taxId || ''}
          onChange={(e) => handleOwnerChange(producerIndex, ownerIndex, 'taxId', e.target.value)}
          fullWidth
          error={getFieldError('taxId')}
          helperText={getErrorMessage('taxId')}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          id={`owner-${producerIndex}-${ownerIndex}-addressLines`}
          label="Address"
          name="addressLines"
          value={owner.addressLines || ''}
          onChange={(e) => handleOwnerChange(producerIndex, ownerIndex, 'addressLines', e.target.value)}
          fullWidth
          error={getFieldError('addressLines')}
          helperText={getErrorMessage('addressLines')}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          id={`owner-${producerIndex}-${ownerIndex}-city`}
          label="City"
          name="city"
          value={owner.city || ''}
          onChange={(e) => handleOwnerChange(producerIndex, ownerIndex, 'city', e.target.value)}
          fullWidth
          error={getFieldError('city')}
          helperText={getErrorMessage('city')}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          id={`owner-${producerIndex}-${ownerIndex}-state`}
          select
          label="State"
          name="state"
          value={owner.state || ''}
          onChange={(e) => handleOwnerChange(producerIndex, ownerIndex, 'state', e.target.value)}
          fullWidth
          error={getFieldError('state')}
          helperText={getErrorMessage('state')}
        >
          <MenuItem value="">
            <em>Select State</em>
          </MenuItem>
          {STATES.map(({ code, name }) => (
            <MenuItem key={code} value={code}>
              {code} - {name}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          id={`owner-${producerIndex}-${ownerIndex}-zip`}
          label="Zip"
          name="zip"
          value={owner.zip || ''}
          onChange={(e) => handleOwnerChange(producerIndex, ownerIndex, 'zip', e.target.value)}
          fullWidth
          error={getFieldError('zip')}
          helperText={getErrorMessage('zip')}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          id={`owner-${producerIndex}-${ownerIndex}-percentOwned`}
          label="Percent Owned"
          name="percentOwned"
          type="number"
          value={owner.percentOwned || 0}
          onChange={(e) => handleOwnerChange(producerIndex, ownerIndex, 'percentOwned', parseFloat(e.target.value))}
          fullWidth
          error={getFieldError('percentOwned')}
          helperText={getErrorMessage('percentOwned')}
          inputProps={{ step: "0.01" }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          id={`owner-${producerIndex}-${ownerIndex}-familyRelation`}
          label="Family Relation"
          name="familyRelation"
          value={owner.familyRelation || ''}
          onChange={(e) => handleOwnerChange(producerIndex, ownerIndex, 'familyRelation', e.target.value)}
          fullWidth
          error={getFieldError('familyRelation')}
          helperText={getErrorMessage('familyRelation')}
        />
      </Grid>
    </Grid>
  );
}

export default Owner;